import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Paper, InputAdornment, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { useResetPasswordMutation } from './services/authenticationApi';
import CircularProgress from '@mui/material/CircularProgress';
import PasswordPolicyField, { PasswordPolicyRules } from '../../components/PasswordPolicyField';
import { getPasswordPolicyRegisterRules, PASSWORD_MAX_LENGTH } from '../../utils/passwordPolicy';

const GradientBackground = styled(Box)(() => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    minHeight: '100%',
    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
    clipPath: 'polygon(0 0, 51% 0, 39% 100%, 0% 100%)',
    '@media (max-width: 900px)': {
        clipPath: 'none'
    }
}));

interface ResetPasswordFormData {
    password: string;
    confirmPassword: string;
    ip: string;
}

const ResetPasswordPage = () => {
    const navigate = useNavigate();
    const [resetPassword, { isLoading }] = useResetPasswordMutation();
    const { register, handleSubmit, formState: { errors }, watch } = useForm<ResetPasswordFormData>();
    const password = watch('password') ?? '';
    const [showNewPassword, setShowConfirmPassword] = useState(false);
    const [isFadingOut, setIsFadingOut] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleResetPassword = async (data: ResetPasswordFormData) => {
        setIsSubmitting(true);
        try {
            const response = await resetPassword({
                password: data.password,
                confirmPassword: data.confirmPassword,
                ip: "string"
            }).unwrap();

            if (response.success) {
                enqueueSnackbar(response.message, { variant: 'success' });
                setIsFadingOut(true);
                setTimeout(() => {
                    navigate('/login');
                }, 400);
            }
        } catch (error: any) {
            const status = error?.status ?? error?.originalStatus;
            if (status === 401) {
                navigate('/login', { replace: true });
                return;
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box className={isFadingOut ? 'fade-out' : ''} sx={{
            position: 'relative',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            width: '100%',
            py: { xs: 1.5, sm: 2 },
            px: { xs: 1.5, sm: 2 },
            overflow: 'hidden',
        }}>
            <Paper
                component="form"
                onSubmit={handleSubmit(handleResetPassword)}
                elevation={3}
                sx={{
                    width: { xs: '92%', sm: '85%', lg: '80%', xl: '51.4%' },
                    maxWidth: '984px',
                    maxHeight: '100%',
                    flexShrink: 1,
                    padding: { xs: '0 0 12px 0', sm: 2, md: 2.5, lg: 3 },
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: 'rgba(255, 255, 255, 0.75)',
                    borderRadius: { xs: '25px', md: '35px' },
                    border: '1.2px solid rgba(0, 0, 0, 0.1)',
                    zIndex: 50,
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    '@media (max-height: 860px) and (min-width: 900px)': {
                        flexDirection: 'column',
                        padding: '16px 20px 20px',
                    },
                }}
            >
                <Box sx={{
                    width: { xs: '40%', sm: '35%', md: '38%' },
                    p: { xs: 1.5, md: 1.5 },
                    color: 'white',
                    flexShrink: 0,
                    '@media (max-height: 860px) and (min-width: 900px)': {
                        width: '30%',
                        maxWidth: 140,
                        p: 1,
                    },
                }}>
                    <Box
                        component="img"
                        src={`${process.env.PUBLIC_URL}/assets/images/logo/newlogo.png`}
                        alt="Logo"
                        sx={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block' }}
                    />
                </Box>

                <Box sx={{
                    width: { xs: '100%', md: '62%' },
                    bgcolor: { xs: 'white', md: 'transparent' },
                    p: { xs: '12px 16px', md: 0 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: { xs: 1, md: 1.25 },
                    borderRadius: { xs: '25px', md: 0 },
                    minHeight: 0,
                    '@media (max-height: 860px) and (min-width: 900px)': {
                        width: '100%',
                        bgcolor: 'white',
                        borderRadius: '25px',
                        p: '12px 16px 16px',
                        gap: 1,
                    },
                }}>
                    <Box sx={{ width: { xs: '100%', sm: '90%', md: '88%' }, mb: 0.5 }}>
                        <Typography sx={{
                            fontWeight: 400,
                            fontSize: '16px',
                            lineHeight: '24px',
                            letterSpacing: '-0.4px',
                            verticalAlign: 'middle',
                            color: '#00236F',
                            mb: 0.5,
                        }}>
                            Şifrenizi Belirleyin
                        </Typography>
                        <Typography sx={{
                            fontSize: { xs: '13px', md: '14px' },
                            color: '#64748b',
                            lineHeight: 1.4,
                        }}>
                            Lütfen yeni ve güçlü bir şifre belirleyin.
                        </Typography>
                    </Box>

                    <Box sx={{
                        width: { xs: '100%', sm: '90%', md: '88%' },
                        display: 'flex',
                        flexDirection: 'column',
                        gap: { xs: 1, md: 1.25 },
                    }}>
                        <PasswordPolicyField
                            fullWidth
                            label="Yeni Şifre"
                            required
                            showStrengthIndicator
                            policyValue={password}
                            {...register('password', getPasswordPolicyRegisterRules('Şifre alanı boş bırakılamaz'))}
                            error={!!errors.password}
                            helperText={errors.password?.message}
                            inputProps={{ maxLength: PASSWORD_MAX_LENGTH }}
                        />

                        <TextField
                            fullWidth
                            label="Yeni Şifre Tekrar"
                            required
                            type={showNewPassword ? 'text' : 'password'}
                            {...register('confirmPassword', {
                                required: 'Yeni şifre tekrarı gerekli',
                                validate: (val: string) => {
                                    if (watch('password') !== val) {
                                        return 'Girilen şifreler eşleşmemektedir.';
                                    }
                                }
                            })}
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword?.message}
                            inputProps={{ maxLength: PASSWORD_MAX_LENGTH }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowConfirmPassword(!showNewPassword)} edge="end">
                                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />

                        <PasswordPolicyRules password={password} compact sx={{ mt: 2.5, mb: 2.5 }} />

                        <Button
                            type="submit"
                            fullWidth
                            disabled={isSubmitting || isLoading}
                            sx={{
                                height: '52px',
                                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                borderColor: '#1e3c72',
                                borderRadius: '12px',
                                color: 'white',
                                fontSize: '15px',
                                fontWeight: 600,
                                textTransform: 'none',
                                boxShadow: '0 4px 16px rgba(30, 60, 114, 0.25)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #1a3569 0%, #25478a 100%)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 24px rgba(30, 60, 114, 0.35)'
                                },
                                '&:disabled': {
                                    background: 'rgba(30, 60, 114, 0.3)',
                                    color: 'rgba(255, 255, 255, 0.6)'
                                },
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        >
                            {isSubmitting || isLoading ? <CircularProgress size={24} color="inherit" /> : 'Şifreyi Belirle'}
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <GradientBackground />

            <style>{`
                .fade-out {
                    animation: fadeOutResetPassword 0.4s forwards;
                }
                @keyframes fadeOutResetPassword {
                    from { opacity: 1; }
                    to { opacity: 0; }
                }
            `}</style>
        </Box>
    );
};

export default ResetPasswordPage;
